import { useEffect, useState } from 'react';

import { PLANNER_STALE_CUTOFF_MS } from '@/sections/insights/aiAssistant/constants/limits';

/**
 * Staleness cutoff for the planner-running gate. Returns `expired`.
 *
 * While `plannerSignal` is true a timer runs; it restarts whenever
 * `activityKey` changes (streaming activity resets the window). If `cutoffMs`
 * elapses with no activity, `expired` becomes true. When `plannerSignal` goes
 * false, or `activityKey` changes, `expired` resets to false.
 *
 * Receipt-time based (the effect re-arms on each activityKey change, so the
 * window is measured from when activity last *arrived*, not from any message
 * timestamp — parsing message timestamps risks NaN).
 *
 * The reset is done with React's supported store-info-from-previous-renders
 * technique (setState during render, NOT in an effect) so it is lint-clean and
 * converges in the same commit; the timeout effect only ever sets `expired`
 * true, mirroring the timeout+cleanup shape of useMessageSending's 60s safety
 * timeout.
 */
export function usePlannerStaleCutoff(
  plannerSignal: boolean,
  activityKey: unknown,
  cutoffMs: number = PLANNER_STALE_CUTOFF_MS
): boolean {
  const [expired, setExpired] = useState(false);
  // The (signal, activity) pair that the current `expired` value was computed
  // against. When either changes, re-arm: reset to not-expired during render.
  const [armedAgainst, setArmedAgainst] = useState<{
    plannerSignal: boolean;
    activityKey: unknown;
  }>({ plannerSignal, activityKey });

  if (
    armedAgainst.plannerSignal !== plannerSignal ||
    armedAgainst.activityKey !== activityKey
  ) {
    setArmedAgainst({ plannerSignal, activityKey });
    if (expired) setExpired(false);
  }

  useEffect(() => {
    if (!plannerSignal) return;
    const timeout = setTimeout(() => setExpired(true), cutoffMs);
    return () => clearTimeout(timeout);
  }, [plannerSignal, activityKey, cutoffMs]);

  return expired;
}
